import dashboardApi from '@/api/dashboard';
import { DashboardPlace } from '@/interfaces/models/dashboard';
import { RootState } from '@/store';
import cloneDeep from 'lodash.clonedeep';
import { ActionContext, ActionTree, GetterTree, MutationTree } from 'vuex';
import objectHelper from '@/utils/objectHelper';
import Vue from 'vue';

const namespaced: boolean = true;

export interface DashboardState {
  places: DashboardPlace[];
  projectHourlyStatistics: {
    total: any,
    [key: string]: any
  };
  projectDailyStatistics: {
    total: any,
    [key: string]: any
  };
  dailyStatisticsForMonth: {
    total: any,
    [key: string]: any
  };
  orderStatistic: {
    orders: any[],
    totalOrdersCount: number,
    totalSpent: number,
    isLastOrdersFetched: boolean,
    page: number,
  },
  timezoneOffsetInSeconds: number;
  isFetchingDashboard: boolean;
  isDashboardWasFetch: boolean;
  isFetchingWeekStats: boolean;
  isWeekStatsWasFetch: boolean;
  isFetchingMonthStats: boolean;
  isMonthStatsWasFetch: boolean;
}

const getDefaultState = (): DashboardState => {
  return {
    places: [],
    projectHourlyStatistics: {
      total: {}
    },
    projectDailyStatistics: {
      total: {}
    },
    dailyStatisticsForMonth: {
      total: {}
    },
    orderStatistic: {
      orders: [],
      totalOrdersCount: 0,
      totalSpent: 0,
      isLastOrdersFetched: false,
      page: 0,
    },
    timezoneOffsetInSeconds: 0,
    isFetchingDashboard: false,
    isDashboardWasFetch: false,
    isFetchingWeekStats: false,
    isWeekStatsWasFetch: false,
    isFetchingMonthStats: false,
    isMonthStatsWasFetch: false,
  };
};

const state: DashboardState = getDefaultState();

const getters: GetterTree<DashboardState, RootState> = {
  getHourlyStatisticsGroupedByDayBySelectedPlaceIds(state) {
    return (selectedPlaceIds: string) => {
      if (state.places.length === selectedPlaceIds.length) {
        return state.projectHourlyStatistics.total;
      }

      let projectHourlyStatistics: {
        [timestamp: string]: {
          [hour: string]: {
            offline: number,
            online: number,
          }
        }
      } = {};

      for (let selectedPlaceId of selectedPlaceIds) {
        const placeHourlyStatisticGroupedByDay = state.projectHourlyStatistics[selectedPlaceId];

        if (undefined === placeHourlyStatisticGroupedByDay) {
          continue;
        }

        if (objectHelper.isEmpty(projectHourlyStatistics)) {
          projectHourlyStatistics = cloneDeep(placeHourlyStatisticGroupedByDay);

          continue;
        }

        for (const dayTimestamp in placeHourlyStatisticGroupedByDay) {
          if (placeHourlyStatisticGroupedByDay.hasOwnProperty(dayTimestamp)) {
            const hourlyStatisticsForDay = placeHourlyStatisticGroupedByDay[dayTimestamp];

            if (!projectHourlyStatistics.hasOwnProperty(dayTimestamp)) {
              projectHourlyStatistics[dayTimestamp] = cloneDeep(hourlyStatisticsForDay);
            } else {
              for (const hour in hourlyStatisticsForDay) {
                if (hourlyStatisticsForDay.hasOwnProperty(hour)) {
                  projectHourlyStatistics[dayTimestamp][hour].offline += hourlyStatisticsForDay[hour].offline;
                  projectHourlyStatistics[dayTimestamp][hour].online += hourlyStatisticsForDay[hour].online;
                }
              }
            }
          }
        }
      }

      return projectHourlyStatistics;
    }
  },
  getDailyStatisticsGroupedByWeekBySelectedPlaceIds(state) {
    return (selectedPlaceIds: string) => {
      if (state.places.length === selectedPlaceIds.length) {
        return state.projectDailyStatistics.total;
      }

      let projectDailyStatistics: {
        [timestamp: string]: {
          [hour: string]: {
            offline: number,
            online: number,
          }
        }
      } = {};

      for (let selectedPlaceId of selectedPlaceIds) {
        const placeDailyStatisticGroupedByWeek = state.projectDailyStatistics[selectedPlaceId];

        if (undefined === placeDailyStatisticGroupedByWeek) {
          continue;
        }

        if (objectHelper.isEmpty(projectDailyStatistics)) {
          projectDailyStatistics = cloneDeep(placeDailyStatisticGroupedByWeek);

          continue;
        }

        for (const dayTimestamp in placeDailyStatisticGroupedByWeek) {
          if (placeDailyStatisticGroupedByWeek.hasOwnProperty(dayTimestamp)) {
            const dailyStatisticsForDay = placeDailyStatisticGroupedByWeek[dayTimestamp];

            if (!projectDailyStatistics.hasOwnProperty(dayTimestamp)) {
              projectDailyStatistics[dayTimestamp] = cloneDeep(dailyStatisticsForDay);
            } else {
              projectDailyStatistics[dayTimestamp].offline += dailyStatisticsForDay.offline;
              projectDailyStatistics[dayTimestamp].online += dailyStatisticsForDay.online;
            }
          }
        }
      }

      return projectDailyStatistics;
    }
  },
  getDailyStatisticsForMonthBySelectedPlaceIds(state) {
    return (selectedPlaceIds: string) => {
      if (state.places.length === selectedPlaceIds.length) {
        return state.dailyStatisticsForMonth.total;
      }

      let dailyStatisticsForMonth: {
        [timestamp: string]: {
          [hour: string]: {
            offline: number,
            online: number,
          }
        }
      } = {};

      for (let selectedPlaceId of selectedPlaceIds) {
        const placeDailyStatisticForMonth = state.dailyStatisticsForMonth[selectedPlaceId];

        if (undefined === placeDailyStatisticForMonth) {
          continue;
        }

        if (objectHelper.isEmpty(dailyStatisticsForMonth)) {
          dailyStatisticsForMonth = cloneDeep(placeDailyStatisticForMonth);

          continue;
        }

        for (const dayTimestamp in placeDailyStatisticForMonth) {
          if (placeDailyStatisticForMonth.hasOwnProperty(dayTimestamp)) {
            const dailyStatisticsForDay = placeDailyStatisticForMonth[dayTimestamp];

            if (!dailyStatisticsForMonth.hasOwnProperty(dayTimestamp)) {
              dailyStatisticsForMonth[dayTimestamp] = cloneDeep(dailyStatisticsForDay);
            } else {
              dailyStatisticsForMonth[dayTimestamp].offline += dailyStatisticsForDay.offline;
              dailyStatisticsForMonth[dayTimestamp].online += dailyStatisticsForDay.online;
            }
          }
        }
      }

      return dailyStatisticsForMonth;
    }
  },
};

const actions: ActionTree<DashboardState, RootState> = {
  async fetchPlaces({ commit, dispatch }: ActionContext<DashboardState, RootState>) {
    if (!state.isDashboardWasFetch && !state.isFetchingDashboard) {
      commit('setIsFetchingDashboard', true);

      return dashboardApi.fetchPlaces()
        .then((response: any) => {
          commit('setTimezoneOffsetInSeconds', response.timezoneOffsetInSeconds);

          dispatch('timer/startTimer', response.todayTimestamp, { root: true });
          dispatch('setProjectHourlyStatistics', response.projectHourlyStatistics);

          const places = response.places.map((place: any) => {
            return {
              ...place,
              stats: {
                current: { orders: response.projectStats.current.orders[place.place.id] },
                old: { orders: response.projectStats.old.orders[place.place.id] }
              }
            };
          });

          commit('setPlaces', places);
          commit('setIsFetchingDashboard', false);
          // commit('setIsDashboardWasFetch', true);
        });
    }
  },
  async fetchWeekStats({ rootState, commit }: ActionContext<DashboardState, RootState>, timestampPeriod: { first: number, last: number }) {
    if (!state.isWeekStatsWasFetch && !state.isFetchingWeekStats) {
      commit('setIsFetchingWeekStats', true);

      return dashboardApi.fetchDailyStats(timestampPeriod.first, timestampPeriod.last)
        .then((response: any) => {
          commit('addDailyStatistics', response);
          commit('setIsFetchingWeekStats', false);
          commit('setIsWeekStatsWasFetch', true);
        });
    }
  },
  async fetchMonthStats({ rootState, commit }: ActionContext<DashboardState, RootState>, timestampPeriod: { first: number, last: number }) {
    if (!state.isMonthStatsWasFetch && !state.isFetchingMonthStats) {
      commit('setIsFetchingMonthStats', true);

      return dashboardApi.fetchDailyStats(timestampPeriod.first, timestampPeriod.last)
        .then((response: any) => {
          commit('addDailyStatisticsForMonth', response);
          commit('setIsFetchingMonthStats', false);
          commit('setIsMonthStatsWasFetch', true);
        });
    }
  },
  async fetchOrders({ commit, state }: ActionContext<DashboardState, RootState>, params: any) {
    if (0 === params.page) {
      commit('setOrdersPage', 0);
    }

    if (!state.orderStatistic.isLastOrdersFetched) {
      const requestParams = {
        ...params,
        page: state.orderStatistic.page,
      };

      return dashboardApi.fetchOrders(requestParams)
        .then((orderStatistic: any) => {
          if (params.isMerge) {
            commit('mergeOrderStatistic', orderStatistic);
          } else {
            commit('setOrderStatistic', orderStatistic);
          }
        });
    }
  },
  async setProjectHourlyStatistics({ dispatch, commit }: ActionContext<DashboardState, RootState>, projectHourlyStatistics: any) {
    const projectDailyStatistics: { [key: string]: { [key: string]: any } } = {};

    for (let placeId in projectHourlyStatistics) {
      if (projectHourlyStatistics.hasOwnProperty(placeId)) {
        const dayStats: { [key: string]: any } = {};

        for (let timestamp in projectHourlyStatistics[placeId]) {
          if (projectHourlyStatistics[placeId].hasOwnProperty(timestamp)) {
            dayStats[timestamp] = projectHourlyStatistics[placeId][timestamp].reduce((dayStats: any, hourlyStats: any) => {
              dayStats.offline += hourlyStats.offline;
              dayStats.online += hourlyStats.online;

              return dayStats;
            }, { offline: 0, online: 0 });
          }
        }

        projectDailyStatistics[placeId] = dayStats;
      }
    }

    commit('setProjectHourlyStatistics', projectHourlyStatistics);
    commit('setProjectDailyStatistics', projectDailyStatistics);
    commit('setDailyStatisticsForMonth', projectDailyStatistics);
  },
};

const mutations: MutationTree<DashboardState> = {
  resetState(state) {
    Object.assign(state, getDefaultState());
  },
  setPlaces(state: DashboardState, places: DashboardPlace[]) {
    state.places = places;
  },
  setProjectHourlyStatistics(state: DashboardState, projectHourlyStatistics: any) {
    state.projectHourlyStatistics = projectHourlyStatistics;
  },
  setOrdersPage(state: DashboardState, page: any) {
    state.orderStatistic.page = page;
    state.orderStatistic.isLastOrdersFetched = false;
    state.orderStatistic.orders = [];
  },
  addDailyStatistics(state: DashboardState, projectDailyStatistics: any) {
    for (let placeId in projectDailyStatistics) {
      if (projectDailyStatistics.hasOwnProperty(placeId)) {
        if (state.projectDailyStatistics.hasOwnProperty(placeId)) {
          state.projectDailyStatistics[placeId] = {
            ...projectDailyStatistics[placeId],
            ...state.projectDailyStatistics[placeId]
          };
        } else {
          Vue.set(state.projectDailyStatistics, placeId, projectDailyStatistics[placeId]);
        }
      }
    }
  },
  addDailyStatisticsForMonth(state: DashboardState, dailyStatisticsForMonth: any) {
    for (let placeId in dailyStatisticsForMonth) {
      if (dailyStatisticsForMonth.hasOwnProperty(placeId)) {
        if (state.dailyStatisticsForMonth.hasOwnProperty(placeId)) {
          state.dailyStatisticsForMonth[placeId] = {
            ...dailyStatisticsForMonth[placeId],
            ...state.dailyStatisticsForMonth[placeId]
          };
        } else {
          Vue.set(state.dailyStatisticsForMonth, placeId, dailyStatisticsForMonth[placeId]);
        }
      }
    }
  },
  setOrderStatistic(state: DashboardState, orderStatistic: any) {
    state.orderStatistic.orders = orderStatistic.orders;
    state.orderStatistic.totalOrdersCount = orderStatistic.totalOrdersCount;
    state.orderStatistic.totalSpent = orderStatistic.totalSpent;
    state.orderStatistic.isLastOrdersFetched = orderStatistic.orders.length < 50;
    state.orderStatistic.page++;
  },
  mergeOrderStatistic(state: DashboardState, orderStatistic: any) {
    state.orderStatistic.orders = state.orderStatistic.orders.concat(orderStatistic.orders);
    state.orderStatistic.totalOrdersCount = orderStatistic.totalOrdersCount;
    state.orderStatistic.totalSpent = orderStatistic.totalSpent;
    state.orderStatistic.isLastOrdersFetched = false;
    state.orderStatistic.page++;
  },
  setProjectDailyStatistics(state: DashboardState, projectDailyStatistics: any) {
    state.projectDailyStatistics = projectDailyStatistics;
  },
  setDailyStatisticsForMonth(state: DashboardState, dailyStatisticsForMonth: any) {
    state.dailyStatisticsForMonth = dailyStatisticsForMonth;
  },
  setTimezoneOffsetInSeconds(state: DashboardState, timezoneOffsetInSeconds: any) {
    state.timezoneOffsetInSeconds = timezoneOffsetInSeconds;
  },
  setIsFetchingDashboard(state, isFetchingDashboard) {
    state.isFetchingDashboard = isFetchingDashboard;
  },
  setIsDashboardWasFetch(state, isDashboardWasFetch) {
    state.isDashboardWasFetch = isDashboardWasFetch;
  },
  setIsFetchingWeekStats(state, isFetchingWeekStats) {
    state.isFetchingWeekStats = isFetchingWeekStats;
  },
  setIsWeekStatsWasFetch(state, isWeekStatsWasFetch) {
    state.isWeekStatsWasFetch = isWeekStatsWasFetch;
  },
  setIsFetchingMonthStats(state, isFetchingMonthStats) {
    state.isFetchingMonthStats = isFetchingMonthStats;
  },
  setIsMonthStatsWasFetch(state, isMonthStatsWasFetch) {
    state.isMonthStatsWasFetch = isMonthStatsWasFetch;
  },
};

export default {
  namespaced,
  state,
  getters,
  actions,
  mutations,
};
